import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWhoAmI } from './useWhoAmI';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { useRefreshSetupChecklist } from './setupChecklist/useRefreshSetupChecklist';
import { useAnalytics } from './analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';

type AcceptInvitationOptions = {
  joinMethod?: string;
  trackSchoolJoined?: boolean;
};

interface IInvitationsComposable {
  acceptInvitation: (code: string, options?: AcceptInvitationOptions) => Promise<number>;
  allInvitations: ComputedRef<Components.Schemas.TenantUserInvitationDto[]>;
  copyInvitationLink: (invitation: Components.Schemas.TenantUserInvitationDto) => Promise<void>;
  inviteTenantUsers: (data: Components.Schemas.AddTenantUserBulkDto) => Promise<void>;
  deleteInvitation: (invitation: Components.Schemas.TenantUserInvitationDto) => Promise<void>;
  renewInvitation: (invitation: Components.Schemas.TenantUserInvitationDto, email: string) => Promise<void>;
  sendTransferOwnershipInvitation: (data: Components.Schemas.TransferOwnershipDto) => Promise<void>;
  getInvitationUrlByCode: (code: string) => Promise<string>;
  PENDING_INVITATION_ID: number; // Is checked in backend: If user has isInvitation = true and user id = -1 -> user will be invited in backend
}

export const useInvitations = (): IInvitationsComposable => {
  const { hasTenantAdminAccess } = usePermissions();
  const { catchError, customSuccessToast, customErrorToast, deleteSuccessToast } = useToast();
  const { copyToClipboard } = useCopyToClipboard();
  const { t } = useI18n();
  const { getWhoAmIWithAuthHandling, refresh: refreshWhoAmI } = useWhoAmI();
  const trialUpsellStore = useTrialUpsellStore();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();
  const { trackEvent } = useAnalytics();

  const getInvitations = async (): Promise<Components.Schemas.TenantUserInvitationDto[]> => {
    if (!hasTenantAdminAccess.value) return [];
    const result = await Api.Routes.Invitation.getActiveInvitationsForCurrentTenant({});
    return result.rows;
  };

  const invitationListQueryKey = computed(() => ['invitations', 'list', hasTenantAdminAccess.value]);
  const queryClient = useQueryClient();

  const { data: dataAdmin } = useQueryWithToast({
    queryKey: invitationListQueryKey,
    queryFn: getInvitations,
  });

  const allInvitations = computed<Components.Schemas.TenantUserInvitationDto[]>(() => {
    return dataAdmin.value ?? [];
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: invitationListQueryKey });
  };

  const acceptInvitation = async (code: string, options?: AcceptInvitationOptions): Promise<number> => {
    return catchError(async () => {
      const response = await Api.Routes.Invitation.accept(null, {
        code,
      });

      if (options?.trackSchoolJoined !== false) {
        trackEvent(AnalyticsEvents.onboarding.schoolJoined, {
          join_method: options?.joinMethod ?? 'invitation_link',
        });
      }

      await getWhoAmIWithAuthHandling();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invitations'] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
      ]);
      return response.userRoleId;
    });
  };

  const copyInvitationLink = async (invitation: Components.Schemas.TenantUserInvitationDto): Promise<void> => {
    assertNotNullOrUndefined(invitation.acceptUrl);
    await copyToClipboard(invitation.acceptUrl, t('teacher.invitationLinkCopied'));
    trackEvent(AnalyticsEvents.team.inviteLinkCopied);
  };

  const sendTransferOwnershipInvitation = async (data: Components.Schemas.TransferOwnershipDto): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.Tenant.startTransferOfOwnership(null, data);
      await Promise.all([refresh(), refreshWhoAmI()]);
      customSuccessToast(t('transferOwnership.invitationSuccess', { email: data.email }));
    });
  };

  const recordSuccessfulInvitations = async (count: number): Promise<void> => {
    trackEvent(AnalyticsEvents.team.membersInvited, { invitation_count: count });
    await refreshSetupChecklist();
    trialUpsellStore.recordTeacherInvitesCreated(count);
    trialUpsellStore.requestEligibilityCheck();
    customSuccessToast(t('teacher.invitationsSuccess', { count }));
  };

  const inviteTenantUsers = async (data: Components.Schemas.AddTenantUserBulkDto): Promise<void> => {
    await catchError(async () => {
      const { invitedCount, skippedDeactivatedEmails } = await Api.Routes.Tenant.addOrCreateTenantUsersBulk(null, data);
      await Promise.all([refresh(), refreshWhoAmI()]);
      if (invitedCount > 0) {
        await recordSuccessfulInvitations(invitedCount);
      }
      if (skippedDeactivatedEmails.length > 0) {
        customErrorToast(t('teacher.invitationsSkippedDeactivated', { emails: skippedDeactivatedEmails.join(', ') }));
      }
    });
  };

  const deleteInvitation = async (invitation: Components.Schemas.TenantUserInvitationDto): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.Invitation.delete({ id: invitation.id });
      await Promise.all([refresh(), refreshWhoAmI()]);
      deleteSuccessToast();
    });
  };

  const renewInvitation = async (
    invitation: Components.Schemas.TenantUserInvitationDto,
    email: string,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.Invitation.renew({ id: invitation.id }, { email });
      await Promise.all([refresh(), refreshWhoAmI()]);
      customSuccessToast(t('teacher.invitationSuccess', { email: invitation.email }));
    });
  };

  const getInvitationUrlByCode = async (code: string): Promise<string> => {
    const url = await Api.Routes.Invitation.getInvitationUrlByCode({ code });
    return url;
  };

  return {
    acceptInvitation,
    allInvitations,
    copyInvitationLink,
    inviteTenantUsers,
    deleteInvitation,
    renewInvitation,
    sendTransferOwnershipInvitation,
    getInvitationUrlByCode,
    PENDING_INVITATION_ID: -1,
  };
};
